import { Component, EventEmitter, forwardRef, Input, Output, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessorBase } from '@shared/bases/control-value-accessor.base';
import { SharedModule } from '@shared/modules/shared.module';
import { Select } from 'primeng/select';

@Component({
	selector: 'app-select',
	imports: [SharedModule],
	templateUrl: './select.component.html',
	styleUrl: './select.component.scss',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => SelectComponent),
			multi: true,
		},
	],
})
export class SelectComponent<T = string | number | boolean> extends ControlValueAccessorBase<T> {
	@ViewChild('selectRef') selectRef!: Select;

	@Input() options: { label: string; value: T; icon?: string; search?: string }[] = [];

	@Input() size: 'small' | 'medium' | 'large' = 'large';
	@Input() placeholder = 'Seleccionar';
	@Input() disabled = false;
	@Input() error = false;
	@Input() errorMessage = 'This field is required';
	@Input() label?: string;
	@Input() showIcon = false;
	@Input() filter = false; // Enable search/filter functionality
	@Input() filterPlaceholder = 'Buscar...'; // Search placeholder
	@Input() filterBy = 'label'; // Option field(s) the filter searches (comma-separated)
	@Input() appendTo: 'body' | null = 'body'; // Allow configuring appendTo behavior
	@Input() loading = false; // Show loading spinner
	@Input() panelStyleClass = ''; // Forward PrimeNG's panelStyleClass to allow targeted overlay CSS per-consumer
	@Input() scrollHeight = '14rem';

	@Output() onShow = new EventEmitter<void>(); // Emitted when dropdown opens
	@Output() onHide = new EventEmitter<void>(); // Emitted when dropdown closes

	selectId = `select-${Math.random().toString(36).substring(2, 11)}`;
	isDropdownOpen = false;

	set selectedOption(value: any) {
		this.value = value;
		this.notifyValueChange(value);
	}

	get selectedOption(): any {
		return this.value;
	}

	onDropdownOpen(): void {
		this.isDropdownOpen = true;
		this.disableScrollListener();
		this.onShow.emit();
	}

	/**
	 * Disables the scroll handler when select dropdown is shown to prevent
	 * the overlay from closing when scrolling inside a dialog.
	 * We override the bindScrollListener method of the internal overlay component.
	 */
	private disableScrollListener(): void {
		if (this.appendTo === null && this.selectRef) {
			const select = this.selectRef as any;

			// Access the internal overlay component
			if (select.overlayViewChild) {
				const overlay = select.overlayViewChild as any;

				// Override bindScrollListener to do nothing
				overlay.bindScrollListener = () => {};

				// Also unbind any existing scroll listener
				if (overlay.scrollHandler) {
					overlay.unbindScrollListener();
				}
			}
		}
	}

	onDropdownClose(): void {
		this.isDropdownOpen = false;
		this.markAsTouched();
		this.onHide.emit();
	}

	onBlur(): void {
		this.markAsTouched();
	}

	/**
	 * Sets the disabled state of the select.
	 */
	override setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
	}
}
